import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  CreditCard,
  Target,
} from "lucide-react";

interface AnalyticsData {
  totalRevenue: number;
  monthlyRecurringRevenue: number;
  revenueGrowth: number;
  activeSubscriptions: number;
  subscriptionGrowth: number;
  averageRevenuePerUser: number;
  churnRate: number;
  planDistribution: {
    basic: number;
    premium: number;
    enterprise: number;
  };
  topPerformingPlans: {
    name: string;
    revenue: number;
    subscribers: number;
    growth: number;
  }[];
}

interface SubscriptionAnalyticsProps {
  data: AnalyticsData;
}

export function SubscriptionAnalytics({ data }: SubscriptionAnalyticsProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
  };

  const getGrowthColor = (value: number) => {
    return value >= 0 ? "text-green-600" : "text-red-600";
  };

  const getGrowthIcon = (value: number) => {
    return value >= 0 ? (
      <TrendingUp className="w-4 h-4" />
    ) : (
      <TrendingDown className="w-4 h-4" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              <span className={getGrowthColor(data.revenueGrowth)}>
                {getGrowthIcon(data.revenueGrowth)} {formatPercentage(data.revenueGrowth)}
              </span>{" "}
              from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MRR</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.monthlyRecurringRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">Monthly recurring revenue</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.activeSubscriptions.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              <span className={getGrowthColor(data.subscriptionGrowth)}>
                {getGrowthIcon(data.subscriptionGrowth)}{" "}
                {formatPercentage(data.subscriptionGrowth)}
              </span>{" "}
              from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ARPU</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.averageRevenuePerUser)}
            </div>
            <p className="text-xs text-muted-foreground">Average revenue per user</p>
          </CardContent>
        </Card>
      </div>

      {/* Plan Distribution */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Plan Distribution</CardTitle>
            <CardDescription>Current subscription breakdown by plan type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary">Basic</Badge>
                  <span className="text-sm font-medium">
                    {data.planDistribution.basic} users
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {(
                    (data.planDistribution.basic / data.activeSubscriptions) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gray-400 h-2 rounded-full"
                  style={{
                    width: `${
                      (data.planDistribution.basic / data.activeSubscriptions) * 100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Badge variant="default">Premium</Badge>
                  <span className="text-sm font-medium">
                    {data.planDistribution.premium} users
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {(
                    (data.planDistribution.premium / data.activeSubscriptions) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{
                    width: `${
                      (data.planDistribution.premium / data.activeSubscriptions) * 100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Badge variant="destructive">Enterprise</Badge>
                  <span className="text-sm font-medium">
                    {data.planDistribution.enterprise} users
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {(
                    (data.planDistribution.enterprise / data.activeSubscriptions) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full"
                  style={{
                    width: `${
                      (data.planDistribution.enterprise / data.activeSubscriptions) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Performing Plans</CardTitle>
            <CardDescription>Revenue and growth by plan</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.topPerformingPlans.map((plan, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-muted rounded-lg"
              >
                <div>
                  <div className="font-medium">{plan.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {plan.subscribers} subscribers
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">{formatCurrency(plan.revenue)}</div>
                  <div className={`text-sm ${getGrowthColor(plan.growth)}`}>
                    {getGrowthIcon(plan.growth)} {formatPercentage(plan.growth)}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Churn Rate */}
      <Card>
        <CardHeader>
          <CardTitle>Churn Rate Analysis</CardTitle>
          <CardDescription>Monthly subscription cancellation rate</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="text-3xl font-bold">{data.churnRate.toFixed(2)}%</div>
            <div className="flex-1">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${
                    data.churnRate > 5
                      ? "bg-red-500"
                      : data.churnRate > 2
                      ? "bg-yellow-500"
                      : "bg-green-500"
                  }`}
                  style={{ width: `${Math.min(data.churnRate * 10, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0%</span>
                <span>5%</span>
                <span>10%</span>
              </div>
            </div>
            <Badge
              variant={
                data.churnRate > 5
                  ? "destructive"
                  : data.churnRate > 2
                  ? "secondary"
                  : "default"
              }
            >
              {data.churnRate > 5 ? "High" : data.churnRate > 2 ? "Medium" : "Low"}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
